import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBookmark,
  faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons';
import ConversationModeSimulator from '../components/ConversationModeSimulator';
import AnimatedTextDisplay from '../components/AnimatedTextDisplay';
import './MyOrbitChannel.css';

const MyOrbitChannel = () => {
  const [posts, setPosts] = useState([
    {
      id: 1,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "Understanding dementia: A primer on changes in brain structure and target area for therapy",
      visualUrl: "/snorkle_vis_thumbnails/neuroanatomy_AD.mp4",
      type: "local_video",
      likes: 247,
      comments: 18,
      audio: "Clinical Study Update",
      tags: 3,
      isLiked: false,
      isSaved: false,
      timestamp: "2 hours ago"
    },
    {
      id: 2,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "Interview on the importance of the trial, via YouTube",
      visualUrl: "https://www.youtube.com/embed/Jqr5c2_dShQ?enablejsapi=1&mute=0&loop=1&playlist=Jqr5c2_dShQ&controls=1&modestbranding=1&playsinline=1",
      type: "youtube",
      likes: 523,
      comments: 42,
      audio: "Original Audio",
      tags: 2,
      isLiked: false,
      isSaved: false,
      timestamp: "4 hours ago"
    },
    {
      id: 3,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "Ask anything about the science of your trial! Orbit will generate a video explainer for you.",
      visualUrl: null,
      type: "conversation",
      likes: 634,
      comments: 89,
      audio: "AI Assistant",
      tags: 0,
      isLiked: false,
      isSaved: false,
      timestamp: "6 hours ago"
    },
    {
      id: 4,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "New research update: Recent breakthrough findings from your study team that advance understanding of treatment mechanisms 🔬",
      visualUrl: null,
      type: "animated_text",
      likes: 892,
      comments: 124,
      audio: "Research Update",
      tags: 0,
      isLiked: false,
      isSaved: false,
      timestamp: "1 week ago"
    },
    {
      id: 5,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "Typical workday for a clinical trial researcher via YouTube",
      visualUrl: "https://www.youtube.com/embed/SzkGyyX7nu4?enablejsapi=1&mute=0&loop=1&playlist=SzkGyyX7nu4&controls=1&modestbranding=1&playsinline=1",
      type: "youtube",
      likes: 447,
      comments: 38,
      audio: "Educational Content",
      tags: 3,
      isLiked: false,
      isSaved: false,
      timestamp: "1 week ago"
    },
    {
      id: 6,
      username: "CNS interactive learning hub",
      userAvatar: "🧬",
      caption: "How will using a smartwatch help develop therapies? See how your daily activity data contributes to breakthrough research 📱⌚",
      visualUrl: "/snorkle_vis_thumbnails/smartwatch_vertical.mp4",
      type: "local_video",
      likes: 389,
      comments: 29,
      audio: "Research Technology",
      tags: 2,
      isLiked: false,
      isSaved: false,
      timestamp: "1 week ago"
    }
  ]);

  const handleLike = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, isLiked: !post.isLiked, likes: post.isLiked ? post.likes - 1 : post.likes + 1 }
        : post
    ));
  };

  const handleSave = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId 
        ? { ...post, isSaved: !post.isSaved }
        : post
    ));
  };

  const [currentPostIndex, setCurrentPostIndex] = useState(0);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        // Scroll to next post
        if (currentPostIndex < posts.length - 1) {
          const nextIndex = currentPostIndex + 1;
          const postElements = document.querySelectorAll('.orbit-post');
          if (postElements[nextIndex]) {
            postElements[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentPostIndex(nextIndex);
          }
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        // Scroll to previous post
        if (currentPostIndex > 0) {
          const prevIndex = currentPostIndex - 1;
          const postElements = document.querySelectorAll('.orbit-post');
          if (postElements[prevIndex]) {
            postElements[prevIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            setCurrentPostIndex(prevIndex);
          }
        }
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        // Check if we're on the "latest science" post (id: 7, index: 6)
        const currentPost = posts[currentPostIndex];
        if (currentPost && currentPost.type === 'animated_text') {
          e.preventDefault();
          // Click the first research button
          const firstButton = document.querySelector('.learn-more-button');
          if (firstButton) {
            firstButton.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPostIndex, posts]);

  // Track which post is currently visible (update currentPostIndex)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postElements = Array.from(document.querySelectorAll('.orbit-post'));
            const index = postElements.indexOf(entry.target);
            if (index !== -1) {
              setCurrentPostIndex(index);
            }
          }
        });
      },
      {
        root: null,
        threshold: 0.5 // Consider a post "current" when 50% visible
      }
    );

    const postElements = document.querySelectorAll('.orbit-post');
    postElements.forEach((el) => observer.observe(el));

    return () => {
      postElements.forEach((el) => observer.unobserve(el));
    };
  }, [posts]);

  return (
    <div className="orbit-channel-container">
      {/* Instagram-style Header (1-way platform - minimal UI) */}
      <div className="orbit-channel-header">
        <div className="orbit-header-left">
          <span className="orbit-logo">Orbit</span>
          <span className="orbit-logo-subtitle">by Snorkle Labs</span>
        </div>
      </div>

      {/* Feed */}
      <div className="orbit-feed">
        {posts.map((post) => (
          <OrbitPost 
            key={post.id} 
            post={post} 
            onLike={handleLike}
            onSave={handleSave}
          />
        ))}
      </div>

      {/* Navigation Bar - Trial-focused */}
      <div className="orbit-nav-bar">
        <div className="orbit-nav-item">
          <div className="orbit-nav-icon">🔍</div>
        </div>
        <div className="orbit-nav-item orbit-nav-active">
          <div className="orbit-nav-icon">🔔</div>
        </div>
        <div className="orbit-nav-item">
          <div className="orbit-nav-avatar">👤</div>
        </div>
      </div>
    </div>
  );
};

// Separate component for each post
const OrbitPost = ({ post, onLike, onSave }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const postRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const currentPostRef = postRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
          
          // Pause/play local videos based on visibility
          if (post.type === 'local_video' && iframeRef.current) {
            if (entry.isIntersecting) {
              iframeRef.current.play().catch(err => console.log('Play error:', err));
            } else {
              iframeRef.current.pause();
            }
          }
          
          // Pause/play YouTube videos based on visibility (with slight delay to ensure iframe is ready)
          if (post.type === 'youtube' && iframeRef.current) {
            setTimeout(() => {
              const command = entry.isIntersecting ? 'playVideo' : 'pauseVideo';
              iframeRef.current.contentWindow?.postMessage(
                JSON.stringify({ event: 'command', func: command, args: [] }),
                '*'
              );
            }, 500);
          }
        });
      },
      {
        root: null,
        threshold: 0.5
      }
    );

    if (currentPostRef) {
      observer.observe(currentPostRef);
    }

    return () => {
      if (currentPostRef) {
        observer.unobserve(currentPostRef);
      }
    };
  }, [post.type]);

  // Callback for video showing in ConversationModeSimulator and AnimatedTextDisplay
  const handleVideoShow = useCallback((showing) => {
    setIsVideoPlaying(showing);
  }, []);

  return (
    <div ref={postRef} className="orbit-post">
      {/* Post Header */}
      <div className="orbit-post-header">
        <div className="orbit-post-user-info">
          <div className="orbit-post-avatar">{post.userAvatar}</div>
          <div className="orbit-post-user-details">
            <span className="orbit-post-username">{post.username}</span>
            <span className="orbit-post-timestamp">{post.timestamp}</span>
          </div>
        </div>
        <div className="orbit-post-follow-section">
          <button className="orbit-follow-button">Follow</button>
          <FontAwesomeIcon icon={faEllipsisH} className="orbit-post-menu" />
        </div>
      </div>

      {/* Visual Content (YouTube, Conversation Mode, Animated Text, or Local Video) */}
      <div className="orbit-post-visual">
        {post.type === 'conversation' ? (
          // Conversation Mode Simulator
          <ConversationModeSimulator 
            isActive={isVisible} 
            onVideoShow={handleVideoShow}
          />
        ) : post.type === 'animated_text' ? (
          // Animated Text Display
          <AnimatedTextDisplay 
            isActive={isVisible}
            onVideoShow={handleVideoShow}
          />
        ) : post.type === 'local_video' ? (
          // Local Video Player with optional text overlay
          <div className="orbit-video-container">
            <video
              ref={iframeRef}
              src={post.visualUrl}
              loop
              playsInline
              className="orbit-video-player"
            />
            {/* Instagram-style text overlay (only for smartwatch video - id 6) */}
            {isVisible && post.id === 6 && (
              <div className="orbit-video-text-overlay">
                <div className="orbit-video-text-overlay-content">
                  <span className="orbit-text-line orbit-text-line-1">How does using a</span>
                  <br />
                  <span className="orbit-text-line orbit-text-line-2 orbit-text-highlight">smartwatch</span>
                  <br />
                  <span className="orbit-text-line orbit-text-line-3">help develop</span>
                  <br />
                  <span className="orbit-text-line orbit-text-line-4 orbit-text-highlight">therapies?</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          // YouTube iframes
          <iframe
            ref={iframeRef}
            src={post.visualUrl}
            title={post.caption}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              backgroundColor: post.type === 'youtube' ? '#000' : 'transparent'
            }}
          />
        )}
      </div>

      {/* Action Buttons - TikTok style on right (minimal for 1-way platform) */}
      {!isVideoPlaying && (
        <div className="orbit-post-actions">
          <div className="orbit-post-actions-left">
            <button 
              className="orbit-action-button"
              onClick={() => onSave(post.id)}
            >
              <FontAwesomeIcon 
                icon={post.isSaved ? faBookmark : faBookmarkRegular}
                className={post.isSaved ? "orbit-saved" : ""}
              />
            </button>
          </div>
        </div>
      )}

      {/* Caption */}
      {!isVideoPlaying && (
        <div className="orbit-post-caption">
          <span className="orbit-caption-username">{post.username}</span>
          <span className="orbit-caption-text">{post.caption}</span>
        </div>
      )}
    </div>
  );
};

export default MyOrbitChannel;
